import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { PopupContext } from 'components';
import { useContext } from 'react';
import { getPageParamFromLastPage } from 'utils/getPageParamFromLastPage';
import PRODUCTS_KEYS from '../products.keys';
import PRODUCTS_MUTATIONS from '../products.mutation';
import PRODUCTS_QUERIES from '../products.queries';

export const useFetchParentProducts = ({ page, pageSize, search, ordering, filters } = {}) =>
  useQuery({
    queryKey: PRODUCTS_KEYS.PRODUCTS_PAGINATED({ page, pageSize, search, ordering, filters }),
    queryFn: () =>
      PRODUCTS_QUERIES.FETCH_PARENT_PRODUCTS({ page, pageSize, search, ordering, filters }),
    staleTime: 1 * 60 * 1000, // 1 minute
  });

export const useFetchParentProductsInfinite = ({ pageSize, search, ordering, filters } = {}) =>
  useInfiniteQuery({
    queryKey: PRODUCTS_KEYS.PRODUCTS_INFINITE({ pageSize, filters, search, ordering }),
    queryFn: ({ pageParam = 1 }) =>
      PRODUCTS_QUERIES.FETCH_PARENT_PRODUCTS({
        page: pageParam,
        pageSize,
        search,
        ordering,
        filters,
      }),
    getNextPageParam: (lastPage) => getPageParamFromLastPage(lastPage),
    staleTime: 1 * 60 * 1000, // 1 minute
  });

export const useCreateParentProduct = () => {
  const queryClient = useQueryClient();
  const { closePopup } = useContext(PopupContext);
  return useMutation({
    mutationFn: PRODUCTS_MUTATIONS.CREATE_PARENT_PRODUCT,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: PRODUCTS_KEYS.PRODUCTS() });
      closePopup?.();
    },
  });
};

export const useUpdateParentProduct = () => {
  const queryClient = useQueryClient();
  const { closePopup } = useContext(PopupContext);
  return useMutation({
    mutationFn: PRODUCTS_MUTATIONS.UPDATE_PARENT_PRODUCT,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: PRODUCTS_KEYS.PRODUCTS() });
      closePopup?.();
    },
  });
};

export const useDeleteParentProduct = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: PRODUCTS_MUTATIONS.DELETE_PARENT_PRODUCT,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: PRODUCTS_KEYS.PRODUCTS() });
      queryClient.invalidateQueries({ queryKey: PRODUCTS_KEYS.ALL_CHILD_PRODUCTS() });
    },
  });
};
